import _ from "lodash";
import React, { useState, useEffect, useCallback } from "react";
import classes from "./TableRow.scss";
import { observeElementInViewport } from "observe-element-in-viewport";
import LearningStandards from "./Components/LearningStandards";
import { Draggable } from "react-beautiful-dnd";
import classNames from "classnames";
import {
  onAddNewNode,
  onLeftIndentNode,
  onRightIndentNode,
  updateNode,
  columnIds,
  pasteBulkBenchmarkItems,
  onBulkAddTags,
  onUpdateNodeGrades,
  onLeftIndentGrades,
  onRightIndentGrades,
  getUniqueUpdatedNodes,
  getUpdatedNodeForDraft,
  getFilteredColumns,
  handleVerticalNavigation,
} from "NodeEditor/Utils";
import CodeCell from "./Components/CodeCell";
import DescriptionCell from "./Components/DescriptionCell";
import TagCell from "./Components/TagCell";
import GradesCell from "./Components/GradesCell";
import QuestionsCount from "./Components/QuestionsCount";
import { scrollIntoView } from "Utils";
import ACLStore from "lib/aclStore";

const TableRow = props => {
  const {
    nodes,
    setNodes,
    nodeId,
    mode,
    maxDepth,
    indexCountArray,
    setNodesRef,
    placeholdersText,
    changeSelectedSidebarItem,
    gradeOptions,
    filters,
    updateNodesInDraft,
    userId,
    currentEditorConfig,
    organizationTags,
    inputForMultiLevelTagsQuery,
    organizationTagsObjectMap,
    isActionDisabled,
    isCoachmarksRunning,
    showSelectionBox,
    onCheckBoxClick,
    isNodeSelected,
    hasChildren,
    maxSidebarDepth,
    isScrollManual,
    isDraggingOver,
    draggingOverWith,
  } = props;
  const [isRowVisible, setIsRowVisible] = useState(false);

  useEffect(() => {
    const target = document.getElementById(nodeId);

    // handler for when target is in viewport
    const inHandler = (entry, unobserve, targetEl) => {
      if (node.depth <= maxSidebarDepth) {
        if (isScrollManual.current) {
          //if max depth is 2, then set the parent also so that the sidebar opens when scrolling upwards
          const params =
            maxSidebarDepth == 2 && node.depth == 2
              ? { 1: node.parent, 2: nodeId }
              : { [node.depth]: nodeId };
          changeSelectedSidebarItem({ params });
        }
      }
      if (!isRowVisible) {
        setIsRowVisible(true);
      } else {
        unobserve();
      }
    };
    // handler for when target is NOT in viewport
    const outHandler = () => {};

    // the returned function, when called, stops tracking the target element in the given viewport
    const unobserveFn = observeElementInViewport(
      target,
      inHandler,
      outHandler,
      {
        // set viewport, if null then it's set to window
        viewport: null,
      }
    );

    return () => {
      unobserveFn();
    };
  }, []);

  const node = nodes[nodeId];
  const {
    code,
    subText,
    depth,
    grades,
    parentId,
    isError = false,
    tags,
    benchmarkQuestionCount,
  } = node;

  //calculate index of the node
  const isPresent = _.findIndex(indexCountArray, id => id == nodeId);
  if (isPresent == -1) {
    indexCountArray.push(nodeId);
  }
  const indexOfNode = _.findIndex(indexCountArray, id => id == nodeId) + 1;
  //checking if the node is disabled for drag drop. if yes, make a light gray background
  const draggingOverWithId = isDraggingOver && draggingOverWith;

  const isDisabled = draggingOverWithId
    ? node.depth > nodes[draggingOverWithId].depth
    : false;
  //give styling
  const rowContainerStyle = classNames({
    [classes.rowContainer]: true,
    [classes.rowContainerDisabled]: isDisabled,
    [classes.rowSelected]: isNodeSelected,
    [classes.isRowDragging]: draggingOverWithId
      ? draggingOverWithId == nodeId
      : false,
    [classes.isRowError]: isError,
    COACHMARKS_SCOPE_SEQUENCE_EDITOR_ROW: indexOfNode == 1,
  });

  const removeError = useCallback(() => {
    if (isError) {
      setNodes(prevNodes => {
        return {
          ...prevNodes,
          [nodeId]: {
            ...prevNodes[nodeId],
            isError: false,
            isChecked: false,
          },
        };
      });
    }
  }, [isError, nodeId, setNodes]);

  //functions related to actions
  //1) Content edit
  const onContentEdit = useCallback(
    ({ params }) => {
      updateNode({ nodeId, setNodes, params });
      //remove any error state for this node.
      removeError();
    },
    [nodeId, setNodes, removeError]
  );

  //2) Left Indent
  const onLeftIndent = useCallback(() => {
    const updatedNodesOnLeftIndent = onLeftIndentNode({
      nodes,
      setNodes,
      nodeId,
      maxDepth,
    });

    if (_.size(updatedNodesOnLeftIndent) > 0) {
      const updatedNodeGrades = onLeftIndentGrades({
        nodes: updatedNodesOnLeftIndent,
        setNodes,
        nodeId,
      });

      const updatedNodes = getUniqueUpdatedNodes({
        previouslyUpdateNodes: updatedNodesOnLeftIndent,
        latestUpdateNodes: updatedNodeGrades,
      });

      updateNodesInDraft({ updatedNodes });
    }
  }, [nodes, setNodes, nodeId, maxDepth, updateNodesInDraft]);

  //3) Right Indent

  const onRightIndent = useCallback(() => {
    const updatedNodesOnRightIndent = onRightIndentNode({
      nodes,
      setNodes,
      nodeId,
      maxDepth,
    });

    if (_.size(updatedNodesOnRightIndent) > 0) {
      const updatedNodeGrades = onRightIndentGrades({
        nodes: updatedNodesOnRightIndent,
        setNodes,
        nodeId,
      });

      const updatedNodes = getUniqueUpdatedNodes({
        previouslyUpdateNodes: updatedNodesOnRightIndent,
        latestUpdateNodes: updatedNodeGrades,
      });

      updateNodesInDraft({ updatedNodes });
    }
  }, [nodes, setNodes, nodeId, maxDepth, updateNodesInDraft]);

  //4) Add new row
  const onAddRow = useCallback(() => {
    const [updatedNodes, newNodeId] = onAddNewNode({
      nodes,
      setNodes,
      nodeId,
      maxDepth,
      userId,
      isActionDisabled,
    });
    setTimeout(() => {
      scrollIntoView({
        element: document.getElementById(newNodeId),
        checkForElementInViewPort: true,
        block: "center",
      });
    }, 300);

    updateNodesInDraft({ updatedNodes });
  }, [
    nodes,
    setNodes,
    maxDepth,
    nodeId,
    updateNodesInDraft,
    userId,
    isActionDisabled,
  ]);

  //5) on checkbox click
  const onCheckboxClick = useCallback(
    ({ prevIsChecked }) => {
      setNodes(prevNodes => {
        return {
          ...prevNodes,
          [nodeId]: { ...prevNodes[nodeId], isChecked: !prevIsChecked },
        };
      });
    },
    [setNodes, nodeId]
  );
  //6) on Add Tags
  const onAddTags = useCallback(
    ({ itemsList }) => {
      const updatedNodes = onBulkAddTags({
        nodes,
        setNodes,
        selectedNodeIds: [nodeId],
        itemsList,
      });
      updateNodesInDraft({ updatedNodes });
    },
    [nodes, setNodes, nodeId, updateNodesInDraft]
  );

  //7) update current node grades and it's child grades
  const onUpdateGrades = useCallback(
    ({ grades, nodeGrades }) => {
      const updatedNodes = onUpdateNodeGrades({
        setNodes,
        nodeId,
        grades,
        nodes,
        nodeGrades,
      });

      //remove the error state for this node.
      removeError();
      updateNodesInDraft({ updatedNodes });
    },
    [setNodes, nodeId, nodes, updateNodesInDraft, removeError]
  );

  //8) Bulk rows that are copied from google docs excel sheet are being paste into NodeEditor.js.

  const onBulkPasteRows = useCallback(
    ({ pastedData, cellName, isNewRow, itemsList }) => {
      const updatedNodes = pasteBulkBenchmarkItems({
        nodes,
        setNodes,
        nodeId,
        pastedData,
        cellName,
        isNewRow,
        itemsList,
        userId,
        isActionDisabled,
      });
      updateNodesInDraft({ updatedNodes });
    },
    [nodes, setNodes, nodeId, updateNodesInDraft, userId, isActionDisabled]
  );
  //we can ignore Eslint warning
  const updateDraftNode = useCallback(
    _.debounce(({ cellName, labelValue }) => {
      const updateNode = getUpdatedNodeForDraft({
        cellName,
        nodeId,
        nodes,
        labelValue,
      });
      updateNodesInDraft({ updatedNodes: updateNode });
    }, 1000),
    [nodes, userId]
  );

  //allowed columns
  const allowedColumns = currentEditorConfig.columns;

  const filteredColumns = getFilteredColumns({ allowedColumns, mode });

  const dragAndDropEnabled = ACLStore.can("FeatureFlag:EnableSNSDND");

  return (
    <Draggable
      key={nodeId}
      draggableId={nodeId}
      index={indexOfNode}
      isDragDisabled={
        mode === "view" || isActionDisabled || !dragAndDropEnabled
      }
    >
      {provided => (
        <tr
          className={rowContainerStyle}
          ref={ref => {
            provided.innerRef(ref);
            depth <= maxSidebarDepth ? setNodesRef({ id: nodeId, ref }) : null;
          }}
          {...provided.draggableProps}
          id={nodeId}
          onKeyDown={handleVerticalNavigation}
          tabIndex={0}
        >
          {_.map(filteredColumns, column => {
            const columnStyle = _.get(column, "style", {});
            switch (column.id) {
              case columnIds.label: {
                return (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <LearningStandards
                      node={node}
                      mode={mode}
                      dragHandleProps={provided.dragHandleProps}
                      onContentEdit={onContentEdit}
                      onLeftIndent={onLeftIndent}
                      onRightIndent={onRightIndent}
                      onAddRow={onAddRow}
                      onCheckboxClick={onCheckboxClick}
                      placeholdersText={placeholdersText}
                      filters={filters}
                      isActionDisabled={isActionDisabled}
                      isError={isError}
                      currentEditorConfig={currentEditorConfig}
                      removeError={removeError}
                      onBulkPasteRows={onBulkPasteRows}
                      isCoachmarksRunning={isCoachmarksRunning}
                      indexOfNode={indexOfNode}
                      updateDraftNode={updateDraftNode}
                      showSelectionBox={showSelectionBox}
                      onCheckBoxClick={onCheckBoxClick}
                      isNodeSelected={isNodeSelected}
                      dragAndDropEnabled={dragAndDropEnabled}
                      testId={`appComponent-nodeEditor-${column.id}`}
                    />
                  </td>
                );
              }
              case columnIds.subText: {
                return (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    {isRowVisible && (
                      <DescriptionCell
                        mode={mode}
                        value={subText}
                        depth={depth}
                        onContentEdit={onContentEdit}
                        filters={filters}
                        onBulkPasteRows={onBulkPasteRows}
                        updateDraftNode={updateDraftNode}
                      />
                    )}
                  </td>
                );
              }
              case columnIds.code: {
                return (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    {isRowVisible && (
                      <CodeCell
                        mode={mode}
                        code={code}
                        depth={depth}
                        onContentEdit={onContentEdit}
                        filters={filters}
                        onBulkPasteRows={onBulkPasteRows}
                        updateDraftNode={updateDraftNode}
                      />
                    )}
                  </td>
                );
              }
              case columnIds.grade: {
                return (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    {isRowVisible && (
                      <GradesCell
                        grades={grades}
                        gradeOptions={gradeOptions}
                        mode={mode}
                        onUpdateGrades={onUpdateGrades}
                        testId={`appComponent-nodeEditor-${nodeId}`}
                      />
                    )}
                  </td>
                );
              }
              case columnIds.tag: {
                return (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    {isRowVisible && (
                      <TagCell
                        organizationTags={organizationTags}
                        mode={mode}
                        tags={tags}
                        inputForMultiLevelTagsQuery={
                          inputForMultiLevelTagsQuery
                        }
                        organizationTagsObjectMap={organizationTagsObjectMap}
                        onAddTags={onAddTags}
                        testId={`appComponent-nodeEditor-${column.id}`}
                      />
                    )}
                  </td>
                );
              }
              case columnIds.starQues: {
                return (
                  <td
                    className={classes.tableCell}
                    key={column.id}
                    style={columnStyle}
                  >
                    <QuestionsCount
                      isLeaf={!hasChildren}
                      benchmarkQuestionCount={benchmarkQuestionCount}
                    />
                  </td>
                );
              }
            }
          })}
        </tr>
      )}
    </Draggable>
  );
};

const checkProps = (prevProps, nextProps) => {
  const avoidRerender =
    prevProps.isNodeSelected == nextProps.isNodeSelected &&
    prevProps.nodes == nextProps.nodes &&
    prevProps.setNodes == nextProps.setNodes &&
    prevProps.rootNodeId == nextProps.rootNodeId &&
    prevProps.mode == nextProps.mode &&
    prevProps.maxDepth == nextProps.maxDepth &&
    JSON.stringify(prevProps.placeholdersText) ==
      JSON.stringify(nextProps.placeholdersText) &&
    JSON.stringify(prevProps.gradeOptions) ==
      JSON.stringify(nextProps.gradeOptions) &&
    prevProps.filterNodesIds == nextProps.filterNodesIds &&
    JSON.stringify(prevProps.filters) == JSON.stringify(nextProps.filters) &&
    prevProps.organizationTags == nextProps.organizationTags &&
    JSON.stringify(prevProps.inputForMultiLevelTagsQuery) ==
      JSON.stringify(nextProps.inputForMultiLevelTagsQuery) &&
    JSON.stringify(prevProps.organizationTagsObjectMap) ==
      JSON.stringify(nextProps.organizationTagsObjectMap) &&
    prevProps.userId == nextProps.userId &&
    JSON.stringify(prevProps.currentEditorConfig) ==
      JSON.stringify(nextProps.currentEditorConfig) &&
    prevProps.isActionDisabled == nextProps.isActionDisabled &&
    prevProps.isCoachmarksRunning == nextProps.isCoachmarksRunning &&
    prevProps.shouldShowSelectionBox == nextProps.shouldShowSelectionBox &&
    prevProps.hasChildren == nextProps.hasChildren &&
    prevProps.maxSidebarDepth == nextProps.maxSidebarDepth &&
    prevProps.isScrollManual == nextProps.isScrollManual &&
    prevProps.isDraggingOver == nextProps.isDraggingOver &&
    prevProps.draggingOverWith == nextProps.draggingOverWith;
  if (nextProps.mode == "edit") {
    return avoidRerender && prevProps.nodes == nextProps.nodes;
  }
  return avoidRerender;
};

export default React.memo(TableRow, checkProps);
